import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Layout from "../components/Layout";
import CallDetailsCard from "../components/grants/calls/CallDetailsCard";
import CallArticlesList, { Article } from "../components/grants/calls/CallArticlesList";
import CreateCallModal from "../components/grants/calls/CreateCallModal";
import AddArticleToCallModal from "../components/grants/calls/AddArticleToCallModal";
import ReportCallModal from "../components/grants/calls/ReportCallModal";
import { BarometerSection } from "../components/grants/calls/barometer";
import { hasBarometerCapability, getDistrictCodes } from "../components/grants/calls/barometer";
import { useBarometer } from "../hooks/useBarometer";
import apiClient from "../services/api";
import { useHotel } from "../contexts/HotelContext";
import { useTurnstileContext } from "../hooks/useTurnstile";
import { TurnstileErrorMessage } from "../components/grants/TurnstileErrorMessage";

interface CallDetails {
  id: string;
  title: string;
  description: string | null;
  status: string;
  callType: string;
  grantType: string | null;
  applicationStartDate: string | null;
  applicationEndDate: string | null;
  announcedAt: string | null;
  fundingRange: {
    min?: number;
    max?: number;
    percentage?: number;
    currency: string;
  } | null;
  allocation: number | null;
  targetRegions: string[];
  targetGroups: string[];
  officialUrl: string | null;
  applicationUrl: string | null;
  program: {
    id: string;
    code: string;
    name: string;
    shortName: string;
    capabilities?: string[];
  } | null;
  competition: {
    id: string;
    name: string;
    code: string | null;
    actionCode: string | null;
    status: string;
  } | null;
  operator: {
    id: string;
    name: string;
    shortName: string | null;
    type: string;
    website: string | null;
  } | null;
  project: {
    id: string;
    name: string;
    projectUrl: string | null;
    regions: string[];
    subregions?: string[];
  } | null;
  articles: Article[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Page component for displaying detailed information about a single grant call.
 * Shows call details card and list of related articles.
 * Admins can add/remove articles from the call.
 */
const GrantsCallDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user, hasModuleEnabled } = useHotel();
  const { isCookieReady } = useTurnstileContext();
  const canWrite = user?.role === "admin" || hasModuleEnabled("admin:kiedynabory:write");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddArticleModalOpen, setIsAddArticleModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  const { data: call, isLoading, error, refetch } = useQuery<CallDetails>({
    queryKey: ["grants-call", id],
    queryFn: async () => {
      const { data } = await apiClient.get(`/grants/calls/${id}`);
      return data;
    },
    // Gate: don't fetch until Turnstile cookie is set (prevents 403 on first render)
    enabled: !!id && isCookieReady,
  });

  // Barometer: fetch deficit professions for calls with LABOUR_BAROMETER capability
  const showBarometer = hasBarometerCapability(call?.program);
  const districtCodes = call ? getDistrictCodes(call) : [];
  const {
    data: barometerData,
    isLoading: isBarometerLoading,
    isError: isBarometerError,
  } = useBarometer(showBarometer ? districtCodes : []);

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleAddArticle = () => {
    setIsAddArticleModalOpen(true);
  };

  const handleCloseAddArticleModal = () => {
    setIsAddArticleModalOpen(false);
  };

  const handleCloseReportModal = () => {
    setIsReportModalOpen(false);
  };

  if (isLoading || !isCookieReady) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="animate-pulse">
            <div className="h-4 bg-neutral-200 rounded w-48 mb-4"></div>
            <div className="h-8 bg-neutral-200 rounded w-96 mb-2"></div>
            <div className="h-4 bg-neutral-200 rounded w-64"></div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-6 bg-neutral-200 rounded w-32"></div>
              <div className="h-4 bg-neutral-200 rounded w-full"></div>
              <div className="h-4 bg-neutral-200 rounded w-3/4"></div>
              <div className="h-4 bg-neutral-200 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !call) {
    return (
      <Layout>
        <div className="space-y-6">
          <nav className="text-sm text-neutral-500">
            <Link to="/grants/calls" className="hover:text-primary-600">
              Nabory
            </Link>
            <span className="mx-2">&gt;</span>
            <span>Błąd</span>
          </nav>

          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 text-center" role="alert">
            <div className="text-red-600 mb-4">
              <svg
                className="h-12 w-12 mx-auto mb-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <p className="font-medium">
                Nie udało się pobrać danych naboru
              </p>
              <TurnstileErrorMessage error={error} />
            </div>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => refetch()}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
              >
                Spróbuj ponownie
              </button>
              <Link
                to="/grants/calls"
                className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50"
              >
                Wróć do listy
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const hasArticles = call.articles && call.articles.length > 0;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Breadcrumbs */}
        <nav className="text-sm text-neutral-500">
          <Link to="/grants/calls" className="hover:text-primary-600">
            Nabory
          </Link>
          <span className="mx-2">&gt;</span>
          <span className="text-neutral-900">{call.title}</span>
        </nav>

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 text-left">{call.title}</h1>
          </div>
          <div className="flex items-center gap-2">
            {/* "Zgłoś problem" — secondary button, visible for all logged-in users, independent of canWrite/isAdmin */}
            <button
              onClick={() => setIsReportModalOpen(true)}
              className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Zgłoś problem
            </button>
            {canWrite && (
              <button
                onClick={handleEdit}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Edytuj
              </button>
            )}
          </div>
        </div>

        {/* Call Details Card */}
        <CallDetailsCard call={call} />

        {/* Articles Section - always visible for admins, otherwise only when non-empty */}
        {(hasArticles || canWrite) && (
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
            {/* Section header */}
            <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-neutral-900 text-left">
                  Artykuły o tym naborze
                </h2>
                <p className="text-sm text-neutral-500 text-left">
                  Źródła informacji o naborze
                </p>
              </div>
              {canWrite && (
                <button
                  onClick={handleAddArticle}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center gap-2"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Dodaj artykuł
                </button>
              )}
            </div>

            {/* Articles list or empty state */}
            {hasArticles ? (
              <CallArticlesList
                articles={call.articles}
                canWrite={canWrite}
                callId={id}
              />
            ) : (
              <div className="p-6 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-neutral-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                  />
                </svg>
                <p className="mt-2 text-neutral-500">
                  Brak powiązanych artykułów
                </p>
                {canWrite && (
                  <p className="mt-1 text-sm text-neutral-400">
                    Kliknij &ldquo;Dodaj artykuł&rdquo;, aby dodać pierwszy artykuł do tego naboru.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Empty articles state for non-admin users (no articles, no write access) */}
        {!hasArticles && !canWrite && (
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 text-center">
            <svg
              className="mx-auto h-12 w-12 text-neutral-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
              />
            </svg>
            <p className="mt-2 text-neutral-500">
              Brak powiązanych artykułów
            </p>
          </div>
        )}

        {/* Barometer Section - deficit professions (only for LABOUR_BAROMETER programs) */}
        {showBarometer && (
          <BarometerSection
            barometerData={barometerData}
            isLoading={isBarometerLoading}
            isError={isBarometerError}
          />
        )}
      </div>

      <CreateCallModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        callId={id}
      />

      {id && (
        <AddArticleToCallModal
          isOpen={isAddArticleModalOpen}
          onClose={handleCloseAddArticleModal}
          callId={id}
        />
      )}

      {/* Report problem modal — available for all logged-in users */}
      {id && call && (
        <ReportCallModal
          callId={id}
          callTitle={call.title}
          isOpen={isReportModalOpen}
          onClose={handleCloseReportModal}
        />
      )}
    </Layout>
  );
};

export default GrantsCallDetails;
